<template>
  <div :class="[
    'font-display h-screen w-full flex items-center justify-center p-4 backdrop-blur-sm',
    darkMode 
      ? 'bg-gray-900 bg-opacity-80' 
      : 'bg-gray-100 bg-opacity-90'
  ]">
    <div :class="[
      'relative w-full max-w-2xl max-h-[90vh] flex flex-col rounded-2xl shadow-2xl overflow-hidden group/design-root border',
      darkMode 
        ? 'bg-surface-dark border-border-dark' 
        : 'bg-white border-gray-200'
    ]">
      <div :class="[
        'flex items-center justify-between px-8 py-6 border-b z-10',
        darkMode 
          ? 'bg-surface-dark border-border-dark' 
          : 'bg-white border-gray-200'
      ]">
        <div>
          <h2 :class="[
            'text-2xl font-bold leading-tight tracking-[-0.015em]',
            darkMode ? 'text-white' : 'text-gray-900'
          ]">{{ $t('onboarding.addTrackingLiveStream') }}</h2>
          <p :class="[
            'text-sm mt-1',
            darkMode ? 'text-text-muted' : 'text-gray-600'
          ]">{{ $t('onboarding.setupDescription') }}</p>
        </div>
        <button @click="goBack" :class="[
          'p-2 rounded-full transition-colors',
          darkMode 
            ? 'hover:bg-gray-800 text-text-muted hover:text-white' 
            : 'hover:bg-gray-100 text-gray-500 hover:text-gray-900'
        ]">
          <span class="material-symbols-outlined text-[24px]">close</span>
        </button>
      </div>
      
      <div :class="[
        'flex-1 overflow-y-auto p-8',
        darkMode ? 'bg-surface-dark' : 'bg-gray-50'
      ]">
        <div class="flex flex-col gap-6 max-w-lg mx-auto">
          <div class="flex flex-col gap-2">
            <label :class="[
              'font-bold text-base',
              darkMode ? 'text-white' : 'text-gray-900'
            ]">{{ $t('onboarding.tiktokLiveRoomId') }}</label>
            <div class="flex gap-3">
              <input 
                v-model="liveRoomId"
                :class="[
                  'flex-1 h-12 rounded-xl border px-4 outline-none transition-all placeholder:text-gray-500 text-base focus:border-primary focus:ring-1 focus:ring-primary',
                  darkMode 
                    ? 'border-border-dark bg-surface-input text-white' 
                    : 'border-gray-300 bg-white text-gray-900'
                ]"
                :placeholder="$t('onboarding.enterId')" 
                type="text"
              />
              <button 
                @click="verifyLiveRoom"
                :class="[
                  'h-12 px-6 rounded-xl font-semibold transition-colors border',
                  darkMode 
                    ? 'bg-button-verify hover:bg-button-verify-hover text-white border-border-dark hover:border-gray-500' 
                    : 'bg-green-600 hover:bg-green-700 text-white border-green-700 hover:border-green-800'
                ]"
                :disabled="isVerifying"
              >
                {{ isVerifying ? $t('onboarding.verifying') : $t('onboarding.verify') }}
              </button>
            </div>
            
            <div class="mt-2 space-y-2">
              <!-- 验证中状态 -->
              <div v-if="isVerifying" :class="[
                'flex items-center gap-2 text-sm',
                darkMode ? 'text-slate-400' : 'text-gray-500'
              ]">
                <svg :class="[
                  'animate-spin h-5 w-5',
                  darkMode ? 'text-slate-400' : 'text-gray-500'
                ]" fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" fill="currentColor"></path>
                </svg>
                <span>{{ $t('onboarding.verifying') }}</span>
              </div>
              
              <!-- 验证成功状态 -->
              <div v-if="verificationStatus === 'success'" class="flex items-center gap-2 text-green-600 dark:text-green-400 mt-1">
                <span class="material-symbols-outlined text-[20px] fill-1">check_circle</span>
                <span :class="[
                  'text-sm font-medium',
                  darkMode ? 'text-white' : 'text-gray-900'
                ]">{{ $t('onboarding.liveRoomVerified') }}</span>
              </div>
              
              <!-- 验证失败状态 -->
              <div v-if="verificationStatus === 'error'" class="flex items-center gap-2 text-red-600 dark:text-red-400 text-sm">
                <span class="material-symbols-outlined text-xl">cancel</span>
                <span>{{ $t('onboarding.invalidLiveRoomId') }}</span>
              </div>
            </div>
          </div>
          
          <div class="space-y-4">
            <div :class="[
              'flex gap-4 p-4 rounded-xl border',
              darkMode 
                ? 'border-border-dark bg-surface-input/50' 
                : 'border-gray-300 bg-gray-100'
            ]">
              <div class="pt-0.5">
                <input 
                  v-model="authAnalysis"
                  :class="[
                    'w-5 h-5 rounded cursor-pointer focus:ring-primary',
                    darkMode 
                      ? 'border-gray-600 bg-surface-input text-primary focus:ring-offset-gray-900' 
                      : 'border-gray-400 bg-white text-blue-600 focus:ring-offset-gray-100'
                  ]"
                  id="auth-analysis" 
                  type="checkbox"
                />
              </div>
              <div class="flex flex-col gap-1">
                <label :class="[
                  'font-bold text-sm cursor-pointer',
                  darkMode ? 'text-white' : 'text-gray-900'
                ]" for="auth-analysis">{{ $t('onboarding.authorizeLiveRoomAnalysis') }}</label>
                <p :class="[
                  'text-sm leading-relaxed',
                  darkMode ? 'text-text-muted' : 'text-gray-600'
                ]">{{ $t('onboarding.authorizeAnalysisDescription') }}</p>
              </div>
            </div>
            
            <div :class="[
              'flex gap-4 p-4 rounded-xl border',
              darkMode 
                ? 'border-border-dark bg-surface-input/50' 
                : 'border-gray-300 bg-gray-100'
            ]">
              <div class="pt-0.5">
                <input 
                  v-model="authInteraction"
                  :class="[
                    'w-5 h-5 rounded cursor-pointer focus:ring-primary',
                    darkMode 
                      ? 'border-gray-600 bg-surface-input text-primary focus:ring-offset-gray-900' 
                      : 'border-gray-400 bg-white text-blue-600 focus:ring-offset-gray-100'
                  ]"
                  id="auth-interaction" 
                  type="checkbox"
                />
              </div>
              <div class="flex flex-col gap-1">
                <label :class="[
                  'font-bold text-sm cursor-pointer',
                  darkMode ? 'text-white' : 'text-gray-900'
                ]" for="auth-interaction">{{ $t('onboarding.authorizeAudienceInteraction') }}</label>
                <p :class="[
                  'text-sm leading-relaxed',
                  darkMode ? 'text-text-muted' : 'text-gray-600'
                ]">{{ $t('onboarding.authorizeInteractionDescription') }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div :class="[
        'px-8 py-6 border-t z-10 flex flex-col gap-3',
        darkMode 
          ? 'bg-surface-dark border-border-dark' 
          : 'bg-white border-gray-200'
      ]">
        <button
          @click="completeSetup"
          class="w-full h-12 rounded-xl bg-primary hover:bg-[#00b058] text-white text-base font-bold transition-all shadow-lg shadow-primary/10 flex items-center justify-center gap-2"
          :disabled="!canCompleteSetup || isBinding || bindingStatus === 'success' || bindingStatus === 'already_bound' || bindingStatus === 'pending'"
          :title="getButtonTooltip()"
        >
          <span>{{ getButtonText() }}</span>
        </button>
        
        <!-- 按钮禁用时的提示信息 -->
        <div v-if="!canCompleteSetup && !isBinding" class="flex flex-col gap-1 items-center">
          <div :class="[
            'flex items-center gap-1.5 text-xs',
            darkMode ? 'text-text-muted' : 'text-gray-500'
          ]">
            <span class="material-symbols-outlined text-[14px]">info</span>
            <span>{{ $t('onboarding.completeRequirements') }}</span>
          </div>
          <div :class="[
            'text-xs space-y-1',
            darkMode ? 'text-text-muted' : 'text-gray-500'
          ]">
            <div v-if="verificationStatus !== 'success'" class="flex items-center gap-1">
              <span class="material-symbols-outlined text-xs">info</span>
              <span>{{ $t('onboarding.verifyLiveRoomId') }}</span>
            </div>
            <div v-if="!authAnalysis" class="flex items-center gap-1">
              <span class="material-symbols-outlined text-xs">info</span>
              <span>{{ $t('onboarding.authorizeLiveRoomAnalysisShort') }}</span>
            </div>
            <div v-if="!authInteraction" class="flex items-center gap-1">
              <span class="material-symbols-outlined text-sm">info</span>
              <span>{{ $t('onboarding.authorizeAudienceInteractionShort') }}</span>
            </div>
          </div>

          <!-- 绑定状态显示 -->
          <div class="mt-2 space-y-2">
            <!-- 绑定中状态 -->
            <div v-if="isBinding" :class="[
              'flex items-center gap-2 text-sm',
              darkMode ? 'text-slate-400' : 'text-gray-500'
            ]">
              <svg :class="[
                'animate-spin h-5 w-5',
                darkMode ? 'text-slate-400' : 'text-gray-500'
              ]" fill="none" viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  fill="currentColor"></path>
              </svg>
              <span>{{ $t('onboarding.bindingLiveRoom') }}</span>
            </div>

            <!-- 绑定成功状态 -->
            <div v-if="bindingStatus === 'success'" class="flex items-center gap-2 text-green-600 dark:text-green-400 text-sm">
              <span class="material-symbols-outlined text-xl">check_circle</span>
              <span>{{ $t('onboarding.liveRoomBoundSuccess') }}</span>
            </div>

            <!-- 绑定失败状态 -->
            <div v-if="bindingStatus === 'error'" class="flex items-center gap-2 text-red-600 dark:text-red-400 text-sm">
              <span class="material-symbols-outlined text-xl">cancel</span>
              <span>{{ $t('onboarding.failedToBind') }}</span>
            </div>

            <!-- 绑定申请审核中状态 -->
            <div v-if="bindingStatus === 'pending'" class="space-y-3">
              <div class="flex items-center gap-2 text-yellow-600 dark:text-yellow-400 text-sm">
                <span class="material-symbols-outlined text-xl">pending</span>
                <span>{{ $t('onboarding.bindingUnderReview') }}</span>
              </div>
              <button @click="goBack"
                :class="[
                  'w-full h-10 px-4 text-sm font-medium rounded-lg hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-primary transition-colors',
                  darkMode 
                    ? 'bg-slate-700 text-white focus:ring-offset-background-dark' 
                    : 'bg-gray-700 text-white focus:ring-offset-gray-100'
                ]">
                {{ $t('onboarding.backToPreviousPage') }}
              </button>
            </div>

            <!-- 账号已绑定状态 -->
            <div v-if="bindingStatus === 'already_bound'" class="flex items-center gap-2 text-blue-600 dark:text-blue-400 text-sm">
              <span class="material-symbols-outlined text-xl">check_circle</span>
              <span>{{ $t('onboarding.alreadyBound') }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import bindingService from '@/services/bindingService'
import authService from '@/services/authService'
import { getApiUrl } from '@/config/apiConfig';

export default {
  name: 'Onboarding',
  data() {
    return {
      liveRoomId: '',
      isVerifying: false,
      verificationStatus: '', // '', 'success', 'error'
      authAnalysis: false,
      authInteraction: false,
      isBinding: false,
      bindingStatus: '', // '', 'success', 'error'
      darkMode: false
    }
  },
  computed: {
    canCompleteSetup() {
      return this.verificationStatus === 'success' && this.authAnalysis && this.authInteraction;
    }
  },
  methods: {
    async verifyLiveRoom() {
      if (!this.liveRoomId.trim()) {
        this.verificationStatus = 'error';
        return;
      }

      this.isVerifying = true;
      this.verificationStatus = '';

      try {
        // 获取 katoken，使用 authService
        const katoken = authService.getToken();
        console.log('验证直播间:', this.liveRoomId.trim(), 'katoken:', katoken ? '已获取' : '未获取');

        // 调用TikTok账户验证接口
        const response = await fetch(`${getApiUrl('TIKTOK_CHECK_ACCOUNT')}?roomId=${encodeURIComponent(this.liveRoomId.trim())}`, {
          method: 'GET',
          headers: {
            'req_app_id': 'LivePlugin',
            'req_app_secret': 'ewMpjg7t4vX5bK13oRZzSB8mEuNGr2Yy',
            'katoken': katoken,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`网络错误: ${response.status}`);
        }

        const data = await response.json();
        console.log('TikTok账号验证接口返回:', data);

        // 根据接口返回的data字段判断验证状态
        if (data && data.success && data.data && data.data.exists === true) {
          this.verificationStatus = 'success';
          console.log('直播间验证成功:', this.liveRoomId.trim());
        } else {
          this.verificationStatus = 'error';
          console.warn('直播间验证失败:', (data && data.errMessage) ? data.errMessage : '未知错误');
        }
      } catch (error) {
        console.error('验证直播间过程中出错:', error);
        this.verificationStatus = 'error';
      } finally {
        this.isVerifying = false;
      }
    },
    
    async completeSetup() {
      if (!this.canCompleteSetup) {
        console.log('无法完成设置，条件不满足:', {
          verificationStatus: this.verificationStatus,
          authAnalysis: this.authAnalysis,
          authInteraction: this.authInteraction
        });
        return;
      }

      this.isBinding = true;
      this.bindingStatus = '';

      try {
        console.log('开始绑定流程，直播间ID:', this.liveRoomId);
        
        // 调用绑定服务
        const bindingData = {
          platform: 'tiktok',
          verificationMethod: 'manual', // 使用手动验证
          creatorAccount: this.liveRoomId // 这个值会作为 creatorAccount 传递给API
        };

        console.log('调用bindingService.bindCreator:', bindingData);
        const result = await bindingService.bindCreator(bindingData);
        console.log('bindingService返回结果:', result);

        if (result.success) {
          // 绑定申请成功提交，检查绑定状态
          const bindingStatus = result.data && result.data.bindingStatus;
          console.log('绑定状态:', bindingStatus);
          
          if (bindingStatus === 1 || bindingStatus === 2) {
            // 绑定状态为1或2表示已绑定或已激活
            this.bindingStatus = 'success';
            
            // 保存设置到本地存储或状态管理
            localStorage.setItem('tiktok-live-room-id', this.liveRoomId);
            localStorage.setItem('auth-analysis', this.authAnalysis.toString());
            localStorage.setItem('auth-interaction', this.authInteraction.toString());
            
            // 立即查询直播状态并跳转到Dashboard
            console.log('绑定成功，立即查询直播状态并跳转');
            this.checkLiveStatusAndNavigate();
          } else if (bindingStatus === 3) {
            // 绑定状态为3表示待审核
            this.bindingStatus = 'pending';
            
            // 注意：绑定申请正在审核中，用户还没有权限查看该直播间
            // 因此不应该将直播间ID保存到本地存储
            // 只有当绑定状态为1或2（已绑定或已激活）时才能保存
            console.log('绑定申请正在审核中，不保存直播间ID到本地存储');
            
            // 清除可能存在的旧缓存
            localStorage.removeItem('tiktok-live-room-id');
            localStorage.removeItem('currentStreamer');
          } else {
            // 其他状态显示申请已提交
            this.bindingStatus = 'pending';
          }
        } else {
          // 检查是否是绑定申请正在审核中的情况
          if (result.errCode === '400' && result.errMessage) {
            if (result.errMessage.includes('绑定申请正在审核中')) {
              this.bindingStatus = 'pending';
            } else if (result.errMessage.includes('该主播账号已绑定')) {
              this.bindingStatus = 'already_bound';
            } else {
              this.bindingStatus = 'error';
            }
          } else {
            this.bindingStatus = 'error';
          }
          console.error('绑定失败:', result.message);
        }
      } catch (error) {
        this.bindingStatus = 'error';
        console.error('绑定过程中出错:', error);
      } finally {
        this.isBinding = false;
      }
    },
    
    goBack() {
      // 返回上一页，使用浏览器的历史记录
      this.$router.go(-1);
    },
    
    // 查询直播状态并跳转到Dashboard
    async checkLiveStatusAndNavigate() {
      try {
        console.log('开始查询直播状态，直播间ID:', this.liveRoomId);
        
        // 获取katoken
        const katoken = authService.getToken();
        if (!katoken) {
          console.error('未获取到用户token，无法查询直播状态');
          // 直接跳转到Dashboard，让Dashboard自己处理状态检查
          this.$router.push('/dashboard');
          return;
        }

        // 查询当前直播状态
        const response = await fetch(`${getApiUrl('LIVE_SESSIONS_ROOM_CURRENT')}/${this.liveRoomId}/current`, {
          method: 'GET',
          headers: {
            'req_app_id': 'LivePlugin',
            'req_app_secret': 'ewMpjg7t4vX5bK13oRZzSB8mEuNGr2Yy',
            'katoken': katoken
          }
        });

        const data = await response.json();
        console.log('直播状态查询结果:', data);

        // 检查直播状态
        if (data.success && data.data && data.data.status === 1) {
          console.log('直播间正在直播中，立即跳转到Dashboard');
          // 直播间正在直播，直接跳转
          this.$router.push('/dashboard');
        } else {
          console.log('直播间当前没有直播，跳转到Dashboard');
          // 直播间没有直播，也跳转到Dashboard（Dashboard会显示无直播状态）
          this.$router.push('/dashboard');
        }
      } catch (error) {
        console.error('查询直播状态失败:', error);
        // 即使查询失败也跳转到Dashboard
        this.$router.push('/dashboard');
      }
    },

    /**
     * 获取按钮文本
     */
    getButtonText() {
      if (this.isBinding) {
        return this.$t('onboarding.binding');
      }
      if (this.bindingStatus === 'success' || this.bindingStatus === 'already_bound') {
        return this.$t('onboarding.trackingStarted');
      }
      if (this.bindingStatus === 'pending') {
        return this.$t('onboarding.underReview');
      }
      return this.$t('onboarding.startTracking');
    },

    /**
     * 获取按钮提示信息
     */
    getButtonTooltip() {
      if (this.isBinding) {
        return this.$t('onboarding.bindingInProgress');
      }
      if (this.bindingStatus === 'success' || this.bindingStatus === 'already_bound') {
        return this.$t('onboarding.trackingHasStarted');
      }
      if (!this.canCompleteSetup) {
        const missingRequirements = [];
        if (this.verificationStatus !== 'success') {
          missingRequirements.push(this.$t('onboarding.verifyLiveRoomId'));
        }
        if (!this.authAnalysis) {
          missingRequirements.push(this.$t('onboarding.authorizeLiveRoomAnalysisShort'));
        }
        if (!this.authInteraction) {
          missingRequirements.push(this.$t('onboarding.authorizeAudienceInteractionShort'));
        }
        return `${this.$t('onboarding.completeRequirements')}: ${missingRequirements.join(', ')}`;
      }
      return this.$t('onboarding.completeSetupProcess');
    }
  },
  mounted() {
    // 检查是否已经完成设置，如果已完成则预填充表单
    const roomId = localStorage.getItem('tiktok-live-room-id');
    const authAnalysis = localStorage.getItem('auth-analysis');
    const authInteraction = localStorage.getItem('auth-interaction');
    
    if (roomId) {
      this.liveRoomId = roomId;
      this.verificationStatus = 'success';
    }
    if (authAnalysis === 'true') {
      this.authAnalysis = true;
    }
    if (authInteraction === 'true') {
      this.authInteraction = true;
    }
    
    // 从localStorage加载深色模式设置
    const savedDarkMode = localStorage.getItem('darkMode');
    if (savedDarkMode !== null) {
      this.darkMode = savedDarkMode === 'true';
    }
  }
}
</script>

<style scoped>
.bg-background-dark {
  background-color: #101622;
}
</style>
